import React, { useCallback, useMemo, forwardRef } from "react";
import { Anchor, Text } from "@mantine/core";
import { useAuthUser } from "react-auth-kit";
import { useLocation } from "react-router-dom";
import { useTabNavigate } from "../../../hooks/useTabNavigate";
import { buildTabLocation } from "../../../utils/tabNavigation";
import { resolveEntityPath } from "../../../utils/resolveEntityPath";

export const ENTITY_TAB_ANCHOR_PLAIN_SX = {
  textDecoration: "none",
  cursor: "pointer",
  color: "inherit",
  "&:hover": {
    textDecoration: "none",
    color: "inherit",
  },
};

const isModifiedClick = (event) =>
  event.ctrlKey ||
  event.metaKey ||
  event.shiftKey ||
  event.altKey ||
  event.button === 1;

const EntityTabAnchor = forwardRef(function EntityTabAnchor(
  {
    entity,
    id,
    label,
    fallback = "-",
    size = "xs",
    weight,
    mode = "edit",
    onClick,
    ...anchorProps
  },
  ref
) {
  const auth = useAuthUser();
  const tabNav = useTabNavigate();
  const location = useLocation();

  const pathname = useMemo(
    () => resolveEntityPath(entity, auth()?.userRole, location.pathname),
    [auth, entity, location.pathname],
  );

  const href = useMemo(() => {
    if (!id || !pathname) return undefined;
    const idStr = id?.toString?.() ?? id;
    return buildTabLocation(pathname, `?id=${idStr}&mode=${mode}`);
  }, [id, pathname, mode]);

  const handleClick = useCallback(
    (e) => {
      onClick?.(e);
      if (!id || !pathname) return;
      if (isModifiedClick(e)) return;
      if (!tabNav.enabled) return;
      e.preventDefault();
      tabNav.openDetail({
        pathname,
        id: id?.toString?.() ?? id,
        mode,
        label: label || fallback,
      });
    },
    [onClick, id, pathname, tabNav, label, fallback, mode],
  );

  if (!id || !label) {
    return (
      <Text size={size} color="dimmed">
        {fallback}
      </Text>
    );
  }

  return (
    <Anchor
      ref={ref}
      href={href}
      size={size}
      weight={weight}
      onClick={handleClick}
      {...anchorProps}
    >
      {label}
    </Anchor>
  );
});

export default EntityTabAnchor;
